import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { Clock, FileText, ChevronRight, CheckCircle, X, Lightbulb, Pencil, ArrowRight, Eye, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from '../lib/safeToast';
import { QRCodeSVG } from 'qrcode.react';
import { getApiErrorMessage } from '../lib/utils';

const getErrorMessage = (e) => getApiErrorMessage(e?.response?.data?.detail, null) || null;

const STEPS = [
  { id: 1, title: 'Business entity', subs: ['Entity type', 'Find your business'] },
  { id: 2, title: 'Business details', subs: ['Industry and revenue', 'Address and online presence'] },
  { id: 3, title: 'Business ownership', subs: ['Beneficial owners', 'Account applicant', 'ID verification'] },
];

const ENTITY_TYPES = ['Company', 'Sole Trader', 'Trust', 'Partnership', 'Other'];

const INDUSTRIES = [
  'Agriculture', 'Computer Software', 'Construction', 'Education', 'FMCG',
  'Financial Services', 'General Services', 'Government', 'Health Services',
  'Hospitality', 'Leisure And Entertainment', 'Manufacturing',
];

const REVENUE_OPTIONS = [
  'Less than $10,000', '$10,000 to $50,000', '$50,000 to $200,000',
  '$200,000 to $500,000', '$500,000 to $1,000,000',
  '$1,000,000 to $5,000,000', 'Over $5,000,000',
];

const COUNTRIES = [
  'Australia', 'New Zealand', 'Singapore', 'United Kingdom', 'United States',
  'Canada', 'Indonesia', 'India', 'China', 'Japan', 'Malaysia', 'Philippines',
  'Thailand', 'Vietnam', 'Hong Kong', 'Germany', 'France', 'Ireland',
];

const AU_STATES = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
];

// Australian address validation for principal place of business
const AU_POSTCODE_BY_STATE = {
  ACT: [[2600, 2618], [2900, 2920]],
  NSW: [[2000, 2599], [2619, 2898], [2921, 2999]],
  NT: [[800, 899], [900, 999]],
  QLD: [[4000, 4999], [9000, 9999]],
  SA: [[5000, 5799], [5800, 5999]],
  TAS: [[7000, 7799], [7800, 7999]],
  VIC: [[3000, 3999], [8000, 8999]],
  WA: [[6000, 6797], [6800, 6999]],
};
function validateBusinessAddress(address) {
  const err = {};
  const line1 = (address?.line1 || '').trim();
  const city = (address?.city || '').trim();
  const state = (address?.state || '').trim();
  const postcode = (address?.postcode || '').trim().replace(/\s/g, '');
  const line2 = (address?.line2 || '').trim();

  if (line1.length < 3) err.line1 = 'Street address must be at least 3 characters';
  else if (line1.length > 200) err.line1 = 'Street address is too long';
  else if (!/^[\d\w\s.,'\-/]+$/i.test(line1)) err.line1 = 'Use only letters, numbers, and common address characters';

  if (city.length < 2) err.city = 'Suburb is required (at least 2 characters)';
  else if (city.length > 100) err.city = 'Suburb is too long';
  else if (!/^[\w\s.'\-]+$/i.test(city)) err.city = 'Suburb can only contain letters, spaces, and hyphens';

  if (!state) err.state = 'Please select a state or territory';
  else if (!AU_STATES.some((s) => s.value === state)) err.state = 'Invalid state';

  if (!/^\d{4}$/.test(postcode)) err.postcode = 'Postcode must be exactly 4 digits (e.g. 2000)';
  else {
    const num = parseInt(postcode, 10);
    const ranges = AU_POSTCODE_BY_STATE[state];
    if (ranges) {
      const ok = ranges.some(([lo, hi]) => num >= lo && num <= hi);
      if (!ok) err.postcode = `Postcode ${postcode} doesn't match ${state}. Please check.`;
    }
  }

  if (line2.length > 50) err.line2 = 'Apartment/suite field is too long';

  return { valid: Object.keys(err).length === 0, errors: err };
}

// ID verification form: personal details + address (format validation)
const NAME_PATTERN = /^[\p{L}\s.'\-]+$/u;
function validateUboIdForm(form) {
  const err = {};
  const first = (form.first || '').trim();
  const last = (form.last || '').trim();
  const middle = (form.middle || '').trim();
  const dob = (form.dob || '').trim();
  const a = form.address || {};

  if (first.length < 1) err.first = 'Legal first name is required';
  else if (first.length > 100) err.first = 'First name is too long';
  else if (!NAME_PATTERN.test(first)) err.first = 'Use only letters, spaces, hyphens, or apostrophes';

  if (last.length < 1) err.last = 'Legal last name is required';
  else if (last.length > 100) err.last = 'Last name is too long';
  else if (!NAME_PATTERN.test(last)) err.last = 'Use only letters, spaces, hyphens, or apostrophes';

  if (middle.length > 100) err.middle = 'Middle name is too long';
  else if (middle.length && !NAME_PATTERN.test(middle)) err.middle = 'Use only letters, spaces, hyphens, or apostrophes';

  if (!dob) err.dob = 'Date of birth is required';
  else {
    const dobMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!dobMatch) err.dob = 'Use format yyyy-mm-dd (e.g. 1990-01-15)';
    else {
      const [, y, m, d] = dobMatch.map(Number);
      const date = new Date(y, m - 1, d);
      if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) err.dob = 'Invalid date';
      else if (date > new Date()) err.dob = 'Date of birth cannot be in the future';
      else if (y < 1900) err.dob = 'Please enter a valid year';
    }
  }

  if (!form.nationality) err.nationality = 'Nationality is required';
  if (!form.id_country) err.id_country = 'ID issuance country is required';
  if (!form.id_type) err.id_type = 'ID type is required';

  const country = (a.country || '').trim();
  if (!country) err.address_country = 'Country or region is required';

  const line1 = (a.line1 || '').trim();
  const city = (a.city || '').trim();
  const state = (a.state || '').trim();
  const postcode = (a.postcode || '').trim().replace(/\s/g, '');

  if (line1.length < 3) err.address_line1 = 'Address line 1 must be at least 3 characters';
  else if (line1.length > 200) err.address_line1 = 'Address line 1 is too long';
  else if (!/^[\d\w\s.,'\-/]+$/i.test(line1)) err.address_line1 = 'Use only letters, numbers, and common address characters';

  if (city.length < 2) err.address_city = 'City is required (at least 2 characters)';
  else if (city.length > 100) err.address_city = 'City is too long';
  else if (!/^[\w\s.'\-]+$/i.test(city)) err.address_city = 'City can only contain letters, spaces, and hyphens';

  if (country === 'Australia') {
    const stateUpper = (state || '').trim().toUpperCase();
    const validState = AU_STATES.some((s) => s.value === stateUpper);
    if (!state) err.address_state = 'State or territory is required (e.g. NSW, VIC)';
    else if (!validState) err.address_state = 'Use a valid Australian state (e.g. NSW, VIC, QLD)';
    if (!/^\d{4}$/.test(postcode)) err.address_postcode = 'Postcode must be 4 digits (e.g. 2000)';
    else if (stateUpper && AU_POSTCODE_BY_STATE[stateUpper]) {
      const num = parseInt(postcode, 10);
      const ranges = AU_POSTCODE_BY_STATE[stateUpper];
      const ok = ranges.some(([lo, hi]) => num >= lo && num <= hi);
      if (!ok) err.address_postcode = `Postcode ${postcode} doesn't match ${stateUpper}. Please check.`;
    }
  } else if (country) {
    if (!state && !postcode) err.address_state = 'State or postal code is required';
    if (postcode.length > 0 && postcode.length < 3) err.address_postcode = 'Postal code is too short';
    if (postcode.length > 20) err.address_postcode = 'Postal code is too long';
  }

  if ((a.line2 || '').trim().length > 50) err.address_line2 = 'Address line 2 is too long';

  return { valid: Object.keys(err).length === 0, errors: err };
}

// Format address for display (business or UBO)
function formatAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const parts = [
    addr.line1,
    addr.line2,
    addr.city,
    addr.state,
    addr.postcode,
    addr.country,
  ].filter(Boolean);
  return parts.join(', ');
}

// Restrict DOB input to digits only and format as yyyy-mm-dd (max 8 digits)
function formatDobInput(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

const OWNER_ROLES = ['Director', 'Beneficial owner', 'Beneficial owner & director', 'Shareholder', 'Company Secretary', 'Other'];

const MAX_DOC_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_DOC_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
const ALLOWED_DOC_EXT = '.png, .jpg, .jpeg, .pdf';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function validateImageDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.width > 0 && img.height > 0);
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });
}

function validatePdfDataUrl(dataUrl) {
  try {
    const b64 = dataUrl.split(',')[1];
    if (!b64 || b64.length < 20) return false;
    const first8 = atob(b64.slice(0, 12));
    return first8.startsWith('%PDF');
  } catch {
    return false;
  }
}

const Onboarding = () => {
  const { api, user, refreshUser } = useAuth();
  const { isLive } = useEnvironment();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [data, setData] = useState({
    entity_type: '',
    business: null,
    industry: '',
    monthly_revenue: '',
    products_services: '',
    address: {},
    websites: [''],
    no_website: false,
    business_countries: [],
    third_party_shareholding: '',
    beneficial_owners: [],
    account_applicant_id: '',
    declaration_accepted: false,
    terms_accepted: false,
    ubo_id_details: {},
    ubo_verified: {},
    supporting_documents: [],
    review_declaration_accepted: false,
    signature_data: null,
  });
  const [abnSearch, setAbnSearch] = useState('');
  const [addressErrors, setAddressErrors] = useState({});
  const [abnResults, setAbnResults] = useState(null);
  const [abnLoading, setAbnLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualBusiness, setManualBusiness] = useState({ abn: '', entityName: '', entityType: '', location: '' });
  const [confirmModal, setConfirmModal] = useState(null);
  const [editOwner, setEditOwner] = useState(null);
  const [editForm, setEditForm] = useState({ role: '', first: '', middle: '', last: '', noLongerRelevant: false });
  const [saving, setSaving] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [uboFormTarget, setUboFormTarget] = useState(null);
  const [uboIdForm, setUboIdForm] = useState({ first: '', middle: '', last: '', dob: '', nationality: '', id_country: '', id_type: '', address: {} });
  const [uboIdFormErrors, setUboIdFormErrors] = useState({});
  const [qrModal, setQrModal] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [reviewPhase, setReviewPhase] = useState(false);
  const [signatureRef, setSignatureRef] = useState(null);
  const [signatureDrawn, setSignatureDrawn] = useState(false);
  const [otherDocSlotCount, setOtherDocSlotCount] = useState(1);
  const [individualsLoading, setIndividualsLoading] = useState(false);
  const [ownershipProgress, setOwnershipProgress] = useState(0);
  const lastAbnFetchedRef = useRef(null);
  const ownershipProgressRef = useRef(null);

  const handleDocUpload = async (file, docType) => {
    if (!file) return;
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${ALLOWED_DOC_EXT}`);
      return;
    }
    if (file.size > MAX_DOC_SIZE_BYTES) {
      toast.error(`File too large. Maximum size is ${MAX_DOC_SIZE_BYTES / 1024 / 1024} MB.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (file.type.startsWith('image/')) {
        const valid = await validateImageDataUrl(dataUrl);
        if (!valid) {
          toast.error(`"${file.name}" could not be opened as an image. Please upload a valid PNG or JPG file.`);
          return;
        }
      } else if (file.type === 'application/pdf') {
        if (!validatePdfDataUrl(dataUrl)) {
          toast.error(`"${file.name}" is not a valid PDF. Please upload a valid PDF file.`);
          return;
        }
      }
      setData((d) => ({
        ...d,
        supporting_documents: [
          ...(d.supporting_documents || []).filter((x) => x.type !== docType),
          { type: docType, name: file.name, size: file.size, mime: file.type, data_url: dataUrl },
        ],
      }));
      toast.success(`${file.name} uploaded successfully`);
    } catch {
      toast.error('Failed to read file. Please try again.');
    }
  };

  const removeDoc = (docType) => {
    const isMandatory = docType === 'asic_registration' || docType === 'home_address';
    setData((d) => ({
      ...d,
      supporting_documents: (d.supporting_documents || []).filter((x) => x.type !== docType),
    }));
    if (isMandatory) {
      toast.warning('Required document removed. You must re-upload it before submitting.');
    } else {
      toast.success('Document removed. You can upload a new one.');
    }
  };

  const removeOtherDocSlot = (idx) => {
    const docType = idx === 0 ? 'other' : `other_${idx}`;
    setData((d) => ({
      ...d,
      supporting_documents: (d.supporting_documents || []).filter((x) => x.type !== docType),
    }));
    setOtherDocSlotCount((c) => Math.max(1, c - 1));
  };

  const canProceedFromStep = () => {
    if (step === 1) {
      if (!data.entity_type) return false;
      if (!data.business || !data.business.EntityName) return false;
      return true;
    }
    if (step === 2) {
      if (subStep === 0) return !!(data.industry && data.monthly_revenue);
      if (subStep === 1) return !!((data.products_services || '').trim().length > 0);
      if (subStep === 2) {
        const { valid } = validateBusinessAddress(data.address || {});
        if (!valid) return false;
        if (!data.no_website) {
          const urls = (data.websites || []).map((u) => (u || '').trim()).filter(Boolean);
          if (urls.length === 0) return false;
        }
        return true;
      }
      if (subStep === 3) return (data.business_countries || []).length > 0;
      return true;
    }
    if (step === 3) {
      if (subStep === 0) {
        const owners = data.beneficial_owners || [];
        const invalidNames = ['Unnamed', 'New individual', ''];
        const allValid = owners.length > 0 && owners.every((o) => o.name && !invalidNames.includes(String(o.name).trim()));
        return allValid && !!(data.third_party_shareholding);
      }
      if (subStep === 1) {
        if (!data.account_applicant_id) return false;
        if (data.account_applicant_id === 'none') return !!data.declaration_accepted;
        return true;
      }
      if (subStep === 2) {
        return individualsToVerify.every((p) => data.ubo_verified?.[p.id]);
      }
      return true;
    }
    return true;
  };

  const canSubmitUboForm = () => validateUboIdForm(uboIdForm).valid;

  const canSubmitReview = () => {
    const docs = data.supporting_documents || [];
    const hasAsic = docs.some((d) => d.type === 'asic_registration');
    const hasHome = docs.some((d) => d.type === 'home_address');
    return hasAsic && hasHome && data.declaration_accepted && data.review_declaration_accepted && signatureDrawn;
  };

  const openEditOwner = (owner, index) => {
    const { first, middle, last } = parseName(owner.name);
    setEditOwner({ ...owner, index });
    setEditForm({ role: owner.role || 'Director', first, middle, last, noLongerRelevant: false });
  };

  const saveEditOwner = () => {
    if (!editOwner) return;
    const name = buildName(editForm.first, editForm.middle, editForm.last);
    if (editForm.noLongerRelevant) {
      setData((d) => ({
        ...d,
        beneficial_owners: (d.beneficial_owners || []).filter((o) => o.id !== editOwner.id),
        account_applicant_id: d.account_applicant_id === editOwner.id ? '' : d.account_applicant_id,
      }));
    } else {
      setData((d) => {
        const owners = [...(d.beneficial_owners || [])];
        const idx = owners.findIndex((o) => o.id === editOwner.id);
        if (idx >= 0) owners[idx] = { ...owners[idx], name, role: editForm.role };
        return { ...d, beneficial_owners: owners };
      });
    }
    setEditOwner(null);
  };

  const parseName = (name) => {
    if (!name || !name.trim()) return { first: '', middle: '', last: '' };
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return { first: parts[0], middle: '', last: '' };
    if (parts.length === 2) return { first: parts[0], middle: '', last: parts[1] };
    return { first: parts[0], middle: parts.slice(1, -1).join(' '), last: parts[parts.length - 1] };
  };

  const buildName = (first, middle, last) => [first, middle, last].filter(Boolean).join(' ').trim() || 'Unnamed';

  const getIndividualsToVerify = () => {
    const owners = data.beneficial_owners || [];
    const appId = data.account_applicant_id;
    if (appId === 'none') {
      return [{ id: 'applicant', name: 'Yourself', role: 'Applicant' }, ...owners];
    }
    const applicant = owners.find((o) => o.id === appId);
    if (!applicant) return owners;
    const others = owners.filter((o) => o.id !== appId);
    return [{ ...applicant, role: `Applicant & ${applicant.role}` }, ...others];
  };

  const individualsToVerify = getIndividualsToVerify();

  const openUboForm = (person) => {
    const details = data.ubo_id_details?.[person.id] || {};
    // For "Yourself" (applicant): use only saved details, never pre-fill from user.name (form should start blank).
    // For others (e.g. Director): pre-fill from person.name.
    const { first, middle, last } = person.id === 'applicant'
      ? { first: '', middle: '', last: '' }
      : parseName(person.name);
    setUboFormTarget(person);
    setUboIdFormErrors({});
    setUboIdForm({
      first: details.first || first || '',
      middle: details.middle || middle || '',
      last: details.last || last || '',
      dob: details.dob || '',
      nationality: details.nationality || 'Australia',
      id_country: details.id_country || 'Australia',
      id_type: details.id_type || 'Australian Driver Licence',
      address: details.address || {},
    });
  };

  const saveUboFormLater = async () => {
    if (!uboFormTarget) return;
    setSaving(true);
    try {
      const details = { ...uboIdForm };
      const updated = { ...(data.ubo_id_details || {}), [uboFormTarget.id]: details };
      await api.post('/onboarding/save', {
        entity_type: data.entity_type,
        business: data.business,
        industry: data.industry,
        monthly_revenue: data.monthly_revenue,
        products_services: data.products_services,
        address: data.address,
        websites: data.websites?.filter(Boolean),
        business_countries: data.business_countries,
        third_party_shareholding: data.third_party_shareholding,
        beneficial_owners: data.beneficial_owners,
        account_applicant_id: data.account_applicant_id,
        declaration_accepted: data.declaration_accepted,
        ubo_id_details: updated,
        ubo_verified: data.ubo_verified,
      });
      setData((d) => ({ ...d, ubo_id_details: updated }));
      toast.success('Saved for later');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const simulateQrVerification = () => {
    setQrModal(null);
    setTimeout(() => setSuccessModal(uboFormTarget?.id), 500);
  };

  const closeSuccessAndReturn = () => {
    if (successModal) {
      setData((d) => ({ ...d, ubo_verified: { ...(d.ubo_verified || {}), [successModal]: true } }));
      setSuccessModal(null);
      setUboFormTarget(null);
    }
  };

  useEffect(() => {
    if (!isLive) navigate('/dashboard');
  }, [isLive, navigate]);

  // Pre-fill address from ABR business when entering Address step (if business has address and address not yet set)
  useEffect(() => {
    if (step !== 2 || subStep !== 2 || !data.business) return;
    const b = data.business;
    const hasBusinessAddress = (b.AddressLine1 || b.State || b.Postcode || b.Suburb);
    const hasExistingAddress = (data.address?.line1 || data.address?.state || data.address?.postcode);
    if (!hasBusinessAddress || hasExistingAddress) return;
    setData((d) => ({
      ...d,
      address: {
        line1: b.AddressLine1 || d.address?.line1 || '',
        line2: b.AddressLine2 || d.address?.line2 || '',
        city: b.Suburb || d.address?.city || '',
        state: b.State || d.address?.state || '',
        postcode: b.Postcode || d.address?.postcode || '',
      },
    }));
  }, [step, subStep, data.business?.Abn]);

  // Fetch individuals from ABR when entering Business ownership step (ABR has legalName for sole traders only; companies get no director names)
  useEffect(() => {
    const abn = data.business?.Abn ? String(data.business.Abn).replace(/\D/g, '') : '';
    if (step !== 3 || subStep !== 0 || !abn || abn.length < 9 || lastAbnFetchedRef.current === abn) return;
    lastAbnFetchedRef.current = abn;
    setIndividualsLoading(true);
    setOwnershipProgress(0);
    if (ownershipProgressRef.current) clearInterval(ownershipProgressRef.current);
    const progressInterval = setInterval(() => {
      setOwnershipProgress((p) => (p >= 90 ? 90 : p + 12));
    }, 400);
    ownershipProgressRef.current = progressInterval;
    api.get(`/onboarding/abn-individuals?abn=${encodeURIComponent(abn)}`)
      .then((res) => {
        const list = res.data?.individuals || [];
        // Always set beneficial_owners from ABR result so we never show stale/dummy names (TEST NAME 1/2)
        setData((d) => ({
          ...d,
          beneficial_owners: list.map((o, i) => ({
            id: o.id || `abr-${i}-${Date.now()}`,
            name: o.name || 'Unnamed',
            role: o.role || 'Director',
          })),
        }));
      })
      .catch(() => {})
      .finally(() => {
        if (ownershipProgressRef.current) {
          clearInterval(ownershipProgressRef.current);
          ownershipProgressRef.current = null;
        }
        setOwnershipProgress(100);
        setIndividualsLoading(false);
      });
  }, [step, subStep, data.business?.Abn, api]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/onboarding/state');
        if (res.data?.data && Object.keys(res.data.data).length) {
          let loaded = { ...res.data.data };
          if (Array.isArray(loaded.beneficial_owners)) {
            loaded.beneficial_owners = loaded.beneficial_owners
              .filter((o) => o && typeof o === 'object' && !('loc' in o && 'msg' in o))
              .map((o) => ({ id: String(o.id || Date.now()), name: String(o.name || 'Unnamed'), role: String(o.role || 'Director') }));
          }
          setData((d) => ({ ...d, ...loaded }));
          // Restore abnResults so "Find your business" shows the saved business
          if (loaded.business && (loaded.business.EntityName || loaded.business.entityName)) {
            setAbnResults(loaded.business);
          }
        }
        if (res.data?.step) setStep(res.data.step);
      } catch {
        // ignore
      }
    };
    load();
  }, [api]);

  const handleAbnLookup = async () => {
    const q = abnSearch.trim().replace(/\s/g, '');
    if (!q) return;
    setAbnLoading(true);
    setAbnResults(null);
    try {
      const digits = q.replace(/\D/g, '');
      const isAbn = digits.length >= 9 && digits.length <= 11;
      const isAcn = digits.length === 9;
      if (isAcn) {
        const res = await api.get(`/onboarding/acn-lookup?acn=${encodeURIComponent(q)}`);
        if (res.data?.EntityName || res.data?.Abn) {
          setAbnResults(res.data);
          setData((d) => ({ ...d, business: res.data }));
        } else {
          setAbnResults({ error: res.data?.Message || 'No results found' });
        }
      } else if (isAbn) {
        const res = await api.get(`/onboarding/abn-lookup?abn=${encodeURIComponent(q)}`);
        if (res.data?.EntityName || res.data?.Abn) {
          setAbnResults(res.data);
          setData((d) => ({ ...d, business: res.data }));
        } else {
          setAbnResults({ error: res.data?.Message || 'No results found' });
        }
      } else {
        const res = await api.get(`/onboarding/abn-search?name=${encodeURIComponent(q)}&max_results=10`);
        const names = res.data?.Names || [];
        const list = Array.isArray(names) ? names : [names].filter(Boolean);
        setAbnResults(list.length ? { searchResults: list } : { error: res.data?.Message || 'No matching names found' });
      }
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Lookup failed');
      setAbnResults({ error: 'Lookup failed' });
    } finally {
      setAbnLoading(false);
    }
  };

  const handleSelectBusinessFromSearch = async (item) => {
    const abn = item?.Abn || item?.abn;
    if (!abn) return;
    setAbnLoading(true);
    try {
      const res = await api.get(`/onboarding/abn-lookup?abn=${encodeURIComponent(String(abn).replace(/\D/g, ''))}`);
      if (res.data?.EntityName || res.data?.Abn) {
        setAbnResults(res.data);
        setData((d) => ({ ...d, business: res.data }));
      }
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Lookup failed');
    } finally {
      setAbnLoading(false);
    }
  };

  const saveAndContinue = async () => {
    if (step === 2 && subStep === 2) {
      const { valid, errors } = validateBusinessAddress(data.address || {});
      if (!valid) {
        setAddressErrors(errors);
        toast.error('Please fix the address fields before continuing.');
        return;
      }
      setAddressErrors({});
    }
    setSaving(true);
    try {
      await api.post('/onboarding/save', {
        entity_type: data.entity_type,
        business: data.business,
        industry: data.industry,
        monthly_revenue: data.monthly_revenue,
        products_services: data.products_services,
        address: data.address,
        websites: data.websites?.filter(Boolean),
        business_countries: data.business_countries,
        third_party_shareholding: data.third_party_shareholding,
        beneficial_owners: data.beneficial_owners,
        account_applicant_id: data.account_applicant_id,
        declaration_accepted: data.declaration_accepted,
        ubo_id_details: data.ubo_id_details,
        ubo_verified: data.ubo_verified,
        supporting_documents: data.supporting_documents,
        review_declaration_accepted: data.review_declaration_accepted,
        signature_data: data.signature_data,
      });
      if (step === 1) {
        setStep(2);
        setSubStep(0);
      } else if (step === 2 && subStep === 0) setSubStep(1);
      else if (step === 2 && subStep === 1) setSubStep(2);
      else if (step === 2 && subStep === 2) setSubStep(3);
      else if (step === 2 && subStep === 3) {
        setStep(3);
        setSubStep(0);
      } else if (step === 3 && subStep === 0) setSubStep(1);
      else if (step === 3 && subStep === 1) setSubStep(2);
      else if (step === 3 && subStep === 2) setReviewPhase(true);
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const sigData = signatureRef?.toDataURL?.('image/png') || null;
      await api.post('/onboarding/save', {
        entity_type: data.entity_type,
        business: data.business,
        industry: data.industry,
        monthly_revenue: data.monthly_revenue,
        products_services: data.products_services,
        address: data.address,
        websites: data.websites?.filter(Boolean),
        business_countries: data.business_countries,
        third_party_shareholding: data.third_party_shareholding,
        beneficial_owners: data.beneficial_owners,
        account_applicant_id: data.account_applicant_id,
        declaration_accepted: data.declaration_accepted,
        ubo_id_details: data.ubo_id_details,
        ubo_verified: data.ubo_verified,
        supporting_documents: data.supporting_documents,
        review_declaration_accepted: data.review_declaration_accepted,
        signature_data: sigData,
      });
      await api.post('/onboarding/complete');
      await refreshUser();
      setConfirmModal(null);
      setReviewPhase(false);
      setShowCongratulations(true);
      toast.success('Application submitted');
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isLive) return null;

  const isWelcome = step === 0;
  if (showCongratulations) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Thanks for your application!</h1>
          <p className="text-xl text-slate-600 mb-8">Your next stop is business without borders.</p>
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-left mb-8">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">We are processing your application</h2>
            <p className="text-slate-600 mb-6">
              Our team is reviewing your information as quickly as possible. This process usually takes 1-3 business days. We may reach out to you by email or phone if we require additional details.
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-[#1C472B] flex items-center justify-center text-white"><CheckCircle className="w-6 h-6" /></div>
                <span className="text-sm font-medium mt-2">Sign up</span>
              </div>
              <div className="flex-1 h-0.5 bg-[#1C472B] mt-[-20px]" />
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-[#1C472B] flex items-center justify-center text-white"><Eye className="w-5 h-5" /></div>
                <span className="text-sm font-medium mt-2">Verification</span>
                <span className="text-xs text-slate-500">In review</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200 mt-[-20px]" />
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-slate-200" />
                <span className="text-sm font-medium mt-2">Approved</span>
              </div>
            </div>
          </div>
          <Button className="h-11 px-8 rounded-xl bg-[#1C472B] hover:bg-[#143620] text-white font-semibold" onClick={() => navigate('/dashboard')}>
            Take me back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  if (reviewPhase) {
    return (
      <Layout compactTop>
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Review your information</h1>
            <p className="text-slate-600">Take a moment to review your details below before submitting your verification.</p>
          </div>
          <div className="space-y-6 mb-8">
            <div className="p-6 rounded-xl border border-slate-200 bg-white">
              <h3 className="font-semibold text-slate-800 mb-3">Your business details</h3>
              <p className="text-sm text-slate-600"><strong>Entity:</strong> {data.business?.EntityName || '—'}</p>
              <p className="text-sm text-slate-600"><strong>ABN:</strong> {data.business?.Abn || '—'}</p>
              <p className="text-sm text-slate-600"><strong>Industry:</strong> {data.industry || '—'}</p>
              <p className="text-sm text-slate-600"><strong>Monthly revenue:</strong> {data.monthly_revenue || '—'}</p>
              {data.products_services && <p className="text-sm text-slate-600 mt-1"><strong>Products/services:</strong> {data.products_services.slice(0, 150)}{data.products_services.length > 150 ? '...' : ''}</p>}
              {data.business_countries?.length > 0 && <p className="text-sm text-slate-600 mt-1"><strong>Business locations:</strong> {data.business_countries.join(', ')}</p>}
              {(formatAddress(data.address) || data.address?.line1) && (
                <p className="text-sm text-slate-600 mt-1"><strong>Address:</strong> {formatAddress(data.address) || data.address.line1}</p>
              )}
            </div>
            <div className="p-6 rounded-xl border border-slate-200 bg-white">
              <h3 className="font-semibold text-slate-800 mb-3">UBO & Applicant details</h3>
              {individualsToVerify.map((p) => {
                const verified = data.ubo_verified?.[p.id];
                return (
                  <div key={p.id} className="py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{p.name} – {p.role}</p>
                      {verified && (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                          <CheckCircle className="w-4 h-4" /> Electronically verified
                        </span>
                      )}
                    </div>
                    {data.ubo_id_details?.[p.id] && (
                      <div className="text-sm text-slate-500 mt-1 space-y-0.5">
                        <p>
                          {[data.ubo_id_details[p.id].first, data.ubo_id_details[p.id].last].filter(Boolean).join(' ')} • DOB: {data.ubo_id_details[p.id].dob || '—'} • {data.ubo_id_details[p.id].nationality || '—'}
                        </p>
                        {formatAddress(data.ubo_id_details[p.id].address) && (
                          <p><strong>Address:</strong> {formatAddress(data.ubo_id_details[p.id].address)}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-6 rounded-xl border border-slate-200 bg-white">
              <h3 className="font-semibold text-slate-800 mb-3">Supporting documents</h3>
              <p className="text-sm text-slate-600 mb-4">Upload the following documents:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Company registration from ASIC</p>
                    <p className="text-xs text-slate-500">Required • PNG, JPG, PDF • Max 2 MB</p>
                  </div>
                  <Input key={`file-asic-${(data.supporting_documents || []).some((d) => d.type === 'asic_registration') ? 'has' : 'empty'}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="max-w-[180px]" onChange={(e) => handleDocUpload(e.target.files?.[0], 'asic_registration')} />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Home address proof</p>
                    <p className="text-xs text-slate-500">Required • PNG, JPG, PDF • Max 2 MB</p>
                  </div>
                  <Input key={`file-home-${(data.supporting_documents || []).some((d) => d.type === 'home_address') ? 'has' : 'empty'}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="max-w-[180px]" onChange={(e) => handleDocUpload(e.target.files?.[0], 'home_address')} />
                </div>
                <div className="mt-6 space-y-4">
                  {Array.from({ length: otherDocSlotCount }, (_, idx) => {
                    const docType = idx === 0 ? 'other' : `other_${idx}`;
                    const label = idx === 0 ? 'Other document' : `Other document (${idx + 1})`;
                    const canRemoveSlot = otherDocSlotCount > 1;
                    return (
                      <div key={docType} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                        <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{label}</p>
                          <p className="text-xs text-slate-500">Optional • PNG, JPG, PDF • Max 2 MB</p>
                        </div>
                        <Input key={`file-${docType}-${(data.supporting_documents || []).some((d) => d.type === docType) ? 'has' : 'empty'}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="max-w-[180px]" onChange={(e) => handleDocUpload(e.target.files?.[0], docType)} />
                        {canRemoveSlot && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                            onClick={() => removeOtherDocSlot(idx)}
                            title="Remove this document slot"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 h-auto text-sm text-[#1C472B] hover:text-[#143620]"
                      onClick={() => setOtherDocSlotCount((c) => Math.min(c + 1, 10))}
                    >
                      + Add another document
                    </Button>
                  </div>
                </div>
              </div>
              {(data.supporting_documents || []).length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-200 space-y-2">
                  <p className="text-sm font-medium text-slate-700 mb-2">Uploaded documents</p>
                  {data.supporting_documents.map((doc, i) => (
                    <div key={`${doc.type}-${i}`} className="flex items-center gap-2 text-sm text-slate-600 group">
                      <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                      <span className="font-medium flex-1 min-w-0 truncate">{doc.name}</span>
                      <span className="text-slate-400 shrink-0">
                        ({doc.size >= 1048576 ? `${(doc.size / 1048576).toFixed(1)} MB` : `${(doc.size / 1024).toFixed(1)} KB`})
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        onClick={() => removeDoc(doc.type)}
                        title="Remove and re-upload"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {data.account_applicant_id !== 'none' && (
              <div className="p-6 rounded-xl border border-slate-200 bg-white">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={data.declaration_accepted}
                    onCheckedChange={(c) => setData((d) => ({ ...d, declaration_accepted: !!c }))}
                  />
                  <span className="text-sm text-slate-700">
                    I declare that I have been authorised to act on behalf of {data.business?.EntityName || 'this business'} and will be able to verify my authority upon account opening.
                  </span>
                </label>
              </div>
            )}
            <div className="p-6 rounded-xl border border-slate-200 bg-white">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={data.review_declaration_accepted}
                  onCheckedChange={(c) => setData((d) => ({ ...d, review_declaration_accepted: !!c }))}
                />
                <span className="text-sm text-slate-700">
                  I confirm that I am authorised to fill out this form and operate the account on behalf of the business, and that the information provided is true and correct.
                </span>
              </label>
            </div>
            <div className="p-6 rounded-xl border border-slate-200 bg-white">
              <h3 className="font-semibold text-slate-800 mb-2">Draw your signature in the box below</h3>
              <canvas
                ref={(el) => setSignatureRef(el)}
                width={600}
                height={150}
                className="w-full max-w-full h-32 border-2 border-slate-200 rounded-lg bg-white cursor-crosshair block"
                style={{ touchAction: 'none' }}
                onMouseDown={(e) => {
                  const el = e.currentTarget;
                  const ctx = el.getContext?.('2d');
                  if (!ctx) return;
                  ctx.strokeStyle = '#0F172A';
                  ctx.lineWidth = 2;
                  ctx.lineCap = 'round';
                  const rect = el.getBoundingClientRect();
                  const scaleX = el.width / rect.width; const scaleY = el.height / rect.height;
                  ctx.beginPath();
                  ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                  const move = (ev) => { ctx.lineTo((ev.clientX - rect.left) * scaleX, (ev.clientY - rect.top) * scaleY); ctx.stroke(); };
                  const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); setSignatureDrawn(true); };
                  window.addEventListener('mousemove', move);
                  window.addEventListener('mouseup', up);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const el = e.currentTarget;
                  const ctx = el.getContext?.('2d');
                  if (!ctx || !e.touches[0]) return;
                  ctx.strokeStyle = '#0F172A';
                  ctx.lineWidth = 2;
                  ctx.lineCap = 'round';
                  const rect = el.getBoundingClientRect();
                  const scaleX = el.width / rect.width; const scaleY = el.height / rect.height;
                  const t = e.touches[0];
                  ctx.beginPath();
                  ctx.moveTo((t.clientX - rect.left) * scaleX, (t.clientY - rect.top) * scaleY);
                  const move = (ev) => { ev.preventDefault(); if (ev.touches[0]) { const tt = ev.touches[0]; ctx.lineTo((tt.clientX - rect.left) * scaleX, (tt.clientY - rect.top) * scaleY); ctx.stroke(); } };
                  const up = () => { window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up); setSignatureDrawn(true); };
                  window.addEventListener('touchmove', move, { passive: false });
                  window.addEventListener('touchend', up);
                }}
              />
              <p className="text-xs text-slate-500 mt-2">By clicking "Submit", I acknowledge that the signature will be the electronic representation of my signature for all purposes.</p>
              <Button variant="link" className="px-0 mt-1 text-[#1C472B] text-sm" onClick={() => { const ctx = signatureRef?.getContext?.('2d'); if (ctx) ctx.clearRect(0, 0, signatureRef.width, signatureRef.height); setSignatureDrawn(false); }}>Clear signature</Button>
            </div>
          </div>
          {!canSubmitReview() && (
            <p className="text-sm text-amber-600 mb-4">
              {(() => {
                const docs = data.supporting_documents || [];
                const hasAsic = docs.some((d) => d.type === 'asic_registration');
                const hasHome = docs.some((d) => d.type === 'home_address');
                if (!hasAsic || !hasHome) {
                  const need = [];
                  if (!hasAsic) need.push('Company registration from ASIC');
                  if (!hasHome) need.push('Home address proof');
                  return `Please re-upload required documents: ${need.join(' and ')}. You must upload these before submitting.`;
                }
                if (!data.declaration_accepted || !data.review_declaration_accepted || !signatureDrawn) {
                  return 'Please accept both declarations and draw your signature to submit.';
                }
                return 'Please complete all required steps to submit.';
              })()}
            </p>
          )}
          <div className="flex gap-4">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setReviewPhase(false)}>Back</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" disabled={!canSubmitReview() || saving} onClick={handleSubmit}>{saving ? 'Submitting...' : 'Submit'}</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout compactTop>
      <div className="max-w-3xl mx-auto">
        {/* Progress indicator - compact, aligned per KYC/B flow */}
        {!isWelcome && (
          <div className="flex items-center gap-2 mb-5">
            {STEPS.map((s) => (
              <div key={s.id} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step > s.id ? 'bg-[#1C472B] text-white' : step === s.id ? 'bg-[#1C472B] text-white' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {step > s.id ? <CheckCircle className="w-5 h-5" /> : s.id}
                </div>
                {s.id < 3 && (
                  <div
                    className={`w-12 md:w-16 h-0.5 ${
                      step > s.id ? 'bg-[#1C472B]' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8">
        {!isWelcome && (
            <div className="flex-1">
              {step === 1 && (subStep === 0 || subStep === 1) && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business entity</h1>
                  <p className="text-slate-600 mb-4">Entity type</p>
                  <p className="text-sm text-slate-600 mb-4">
                    You may need to submit relevant business documents for verification, depending on your business entity type.
                  </p>
                  <a href="#" className="text-[#1C472B] text-sm mb-4 block hover:text-[#143620] hover:underline">I don't know my entity type</a>
                  <RadioGroup value={data.entity_type} onValueChange={(v) => setData((d) => ({ ...d, entity_type: v }))}>
                    <div className="flex flex-col gap-5 mb-8">
                      {ENTITY_TYPES.map((e) => (
                        <div
                          key={e}
                          role="button"
                          tabIndex={0}
                          onClick={() => setData((d) => ({ ...d, entity_type: e }))}
                          onKeyDown={(ev) => ev.key === 'Enter' && setData((d) => ({ ...d, entity_type: e }))}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            data.entity_type === e ? 'border-[#1C472B] bg-[#E6F7F3]/50' : 'border-slate-200 hover:border-[#A5E0D2]'
                          }`}
                        >
                          <RadioGroupItem value={e} id={e} />
                          <span className="font-medium text-[#0F172A]">{e}</span>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>

                  {/* Find your business - shows when entity type is selected */}
                  {data.entity_type && (
                    <>
                      <hr className="border-slate-200 my-6" />
                      <p className="text-slate-600 font-medium mb-2">Find your business</p>
                      <p className="text-sm text-slate-600 mb-2">
                        Search by business name, Australian Business Number (ABN) or Australian Company Number (ACN).
                      </p>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Business name, ABN or ACN"
                      value={abnSearch}
                      onChange={(e) => setAbnSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAbnLookup()}
                    />
                    <Button onClick={handleAbnLookup} disabled={abnLoading} className="h-10 px-4 rounded-lg bg-[#1C472B] text-white hover:bg-[#143620] font-semibold disabled:opacity-60">
                      {abnLoading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>
                  {abnResults?.error && <p className="text-amber-600 mb-2">{typeof abnResults.error === 'string' ? abnResults.error : 'Lookup failed'}</p>}
                  {abnResults?.searchResults && abnResults.searchResults.length > 0 && (
                    <div className="rounded-lg border p-4 mb-4">
                      <p className="font-semibold text-slate-700 mb-2">Select your business:</p>
                      <div className="space-y-2">
                        {abnResults.searchResults.map((item, i) => {
                          const abn = item?.Abn || item?.abn;
                          const name = item?.OrganisationName || item?.organisationName || item?.Name || (typeof item === 'string' ? item : null);
                          const label = name || (abn ? `ABN: ${abn}` : `Result ${i + 1}`);
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => abn && handleSelectBusinessFromSearch(item)}
                              disabled={!abn || abnLoading}
                              className="w-full text-left p-3 rounded-lg border hover:border-[#1C472B] hover:bg-[#E6F7F3] transition-colors disabled:opacity-50"
                            >
                              <span className="font-medium">{label}</span>
                              {abn && <span className="text-slate-500 text-sm ml-2">ABN: {abn}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {abnResults?.EntityName && (
                    <div className="rounded-lg border p-4 bg-[#E6F7F3] border-[#A5E0D2] mb-4">
                      <p className="font-semibold text-[#1C472B] mb-1">We found your business!</p>
                      <p><strong>ABN:</strong> {abnResults.Abn}</p>
                      <p><strong>Entity name:</strong> {abnResults.EntityName}</p>
                      <p><strong>Entity type:</strong> {abnResults.EntityType}</p>
                      <p><strong>Location:</strong> {abnResults.Location || abnResults.State}</p>
                    </div>
                  )}
                  <p className="text-sm text-slate-500 mb-2">Can't find you? Contact our support team for help.</p>
                  <Button
                    variant="link"
                    className="px-0 h-auto text-sm"
                    onClick={() => setShowManualEntry((v) => !v)}
                  >
                    {showManualEntry ? 'Hide manual entry' : 'Or enter details manually'}
                  </Button>
                  {showManualEntry && (
                    <div className="rounded-lg border p-4 mt-2 mb-4 bg-slate-50 space-y-3">
                      <p className="text-sm font-medium text-slate-700">Enter your business details</p>
                      <Input
                        placeholder="ABN or ACN (optional)"
                        value={manualBusiness.abn}
                        onChange={(e) => setManualBusiness((m) => ({ ...m, abn: e.target.value }))}
                      />
                      <Input
                        placeholder="Entity name"
                        value={manualBusiness.entityName}
                        onChange={(e) => setManualBusiness((m) => ({ ...m, entityName: e.target.value }))}
                      />
                      <Input
                        placeholder="Entity type (e.g. Australian Private Company)"
                        value={manualBusiness.entityType}
                        onChange={(e) => setManualBusiness((m) => ({ ...m, entityType: e.target.value }))}
                      />
                      <Input
                        placeholder="Location (e.g. NSW, 2000)"
                        value={manualBusiness.location}
                        onChange={(e) => setManualBusiness((m) => ({ ...m, location: e.target.value }))}
                      />
                      <Button
                        size="sm"
                        className="h-9 px-4 rounded-lg bg-[#1C472B] text-white hover:bg-[#143620] font-semibold"
                        onClick={() => {
                          const b = {
                            Abn: manualBusiness.abn || '00000000000',
                            EntityName: manualBusiness.entityName || 'Manual Entry',
                            EntityType: manualBusiness.entityType || 'Other',
                            Location: manualBusiness.location || '',
                          };
                          setData((d) => ({ ...d, business: b }));
                          setAbnResults(b);
                          setShowManualEntry(false);
                        }}
                      >
                        Use these details
                      </Button>
                    </div>
                  )}
                  <p className="text-sm text-slate-500 mt-4">By proceeding you confirm that you accept the Terms & Conditions.</p>
                    </>
                  )}
                </>
              )}

              {step === 2 && subStep === 0 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business details</h1>
                  <p className="text-slate-600 mb-4">Industry</p>
                  <p className="text-sm text-slate-600 mb-2">What industry does your business operate in?</p>
                  <Select value={data.industry} onValueChange={(v) => setData((d) => ({ ...d, industry: v }))}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="Please select an industry" /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-slate-600 mt-6 mb-2">Monthly revenue</p>
                  <p className="text-sm text-slate-600 mb-2">
                    We collect this information to personalise your experience and help you get the most out of your payments.
                  </p>
                  <RadioGroup value={data.monthly_revenue} onValueChange={(v) => setData((d) => ({ ...d, monthly_revenue: v }))}>
                    <div className="space-y-2">
                      {REVENUE_OPTIONS.map((r) => (
                        <div
                          key={r}
                          role="button"
                          tabIndex={0}
                          onClick={() => setData((d) => ({ ...d, monthly_revenue: r }))}
                          onKeyDown={(ev) => ev.key === 'Enter' && setData((d) => ({ ...d, monthly_revenue: r }))}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            data.monthly_revenue === r ? 'border-[#1C472B] bg-[#E6F7F3]/50' : 'border-slate-200 hover:border-[#A5E0D2]'
                          }`}
                        >
                          <RadioGroupItem value={r} id={r} />
                          <span className="font-medium text-[#0F172A]">{r}</span>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </>
              )}

              {step === 2 && subStep === 1 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business details</h1>
                  <p className="text-slate-600 mb-4">Products or services</p>
                  <p className="text-sm text-slate-600 mb-4">
                    Describe your products, including items sold on eCommerce marketplace platforms such as Amazon or Shopify, offline retail store offerings, and any services provided to customers.
                  </p>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4 flex gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-700 mb-1">Sample product description:</p>
                      <p className="text-sm text-slate-600">
                        We are an offline retail business selling women's beauty products, such as lip balms and eyeshadow palettes. Most customers are individuals through Amazon, with occasional sales to local beauty salons.
                      </p>
                    </div>
                    <Lightbulb className="w-6 h-6 text-amber-500 shrink-0" />
                  </div>
                  <Label htmlFor="products" className="block font-medium text-slate-700 mb-2">Product description</Label>
                  <textarea
                    id="products"
                    placeholder="Describe the products or services you sell"
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C472B] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    value={data.products_services || ''}
                    onChange={(e) => setData((d) => ({ ...d, products_services: e.target.value }))}
                  />
                </>
              )}

              {step === 2 && subStep === 2 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business details</h1>
                  <p className="text-slate-600 mb-2">Address and online presence</p>
                  <p className="text-sm text-slate-600 mb-1">Help us understand the location from where you conduct most of your business.</p>
                  <p className="text-sm text-slate-600 mb-4">You can update this later if your address changes.</p>
                  <a href="#" className="text-[#1C472B] text-sm mb-4 block hover:text-[#143620] hover:underline">Which address should I provide?</a>
                  <p className="text-sm text-slate-600 mb-2">What is your principal place of business?</p>
                  <div className="rounded-xl border-2 border-slate-200 bg-white p-4 mb-4 space-y-3">
                    <div>
                      <Label className="text-slate-700 text-sm">Apartment, suite, other (optional)</Label>
                      <Input
                        placeholder="e.g. 207"
                        className={`mt-1 ${addressErrors.line2 ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                        value={data.address?.line2 || ''}
                        onChange={(e) => {
                          setData((d) => ({ ...d, address: { ...d.address, line2: e.target.value } }));
                          if (addressErrors.line2) setAddressErrors((e) => ({ ...e, line2: undefined }));
                        }}
                      />
                      {addressErrors.line2 && <p className="text-xs text-amber-600 mt-1">{addressErrors.line2}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm">Street address</Label>
                      <Input
                        placeholder="e.g. 28 Riddell Parade"
                        className={`mt-1 ${addressErrors.line1 ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                        value={data.address?.line1 || ''}
                        onChange={(e) => {
                          setData((d) => ({ ...d, address: { ...d.address, line1: e.target.value } }));
                          if (addressErrors.line1) setAddressErrors((e) => ({ ...e, line1: undefined }));
                        }}
                        onBlur={() => setAddressErrors((prev) => ({ ...prev, ...validateBusinessAddress(data.address || {}).errors }))}
                      />
                      {addressErrors.line1 && <p className="text-xs text-amber-600 mt-1">{addressErrors.line1}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm">Suburb</Label>
                      <Input
                        placeholder="e.g. Elsternwick"
                        className={`mt-1 ${addressErrors.city ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                        value={data.address?.city || ''}
                        onChange={(e) => {
                          setData((d) => ({ ...d, address: { ...d.address, city: e.target.value } }));
                          if (addressErrors.city) setAddressErrors((e) => ({ ...e, city: undefined }));
                        }}
                        onBlur={() => setAddressErrors((prev) => ({ ...prev, ...validateBusinessAddress(data.address || {}).errors }))}
                      />
                      {addressErrors.city && <p className="text-xs text-amber-600 mt-1">{addressErrors.city}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700 text-sm">State/territory</Label>
                        <Select
                          value={data.address?.state || ''}
                          onValueChange={(v) => {
                            setData((d) => ({ ...d, address: { ...d.address, state: v } }));
                            if (addressErrors.state) setAddressErrors((e) => ({ ...e, state: undefined }));
                            setAddressErrors((prev) => ({ ...prev, ...validateBusinessAddress({ ...data.address, state: v } || {}).errors }));
                          }}
                        >
                          <SelectTrigger className={`mt-1 ${addressErrors.state ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {AU_STATES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {addressErrors.state && <p className="text-xs text-amber-600 mt-1">{addressErrors.state}</p>}
                      </div>
                      <div>
                        <Label className="text-slate-700 text-sm">Postcode</Label>
                        <Input
                          placeholder="e.g. 3185"
                          className={`mt-1 ${addressErrors.postcode ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                          value={data.address?.postcode || ''}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setData((d) => ({ ...d, address: { ...d.address, postcode: v } }));
                            if (addressErrors.postcode) setAddressErrors((e) => ({ ...e, postcode: undefined }));
                          }}
                          onBlur={() => setAddressErrors((prev) => ({ ...prev, ...validateBusinessAddress(data.address || {}).errors }))}
                        />
                        {addressErrors.postcode && <p className="text-xs text-amber-600 mt-1">{addressErrors.postcode}</p>}
                      </div>
                    </div>
                    <Button
                      variant="link"
                      className="px-0 h-auto text-sm text-[#1C472B] hover:text-[#143620]"
                      onClick={() => setData((d) => ({ ...d, address: {} }))}
                    >
                      Search another address
                    </Button>
                  </div>
                  <p className="text-slate-600 mb-2">What is your business website?</p>
                  <p className="text-sm text-slate-600 mb-2">
                    Providing links to your business websites, online store, or social media helps us verify your business quickly.
                  </p>
                  <div className="space-y-2 mb-2">
                    {(data.websites || ['']).map((url, i) => (
                      <Input
                        key={i}
                        id={i === 0 ? 'website-0' : undefined}
                        placeholder="http://www.example.com"
                        className="mb-0"
                        value={url}
                        onChange={(e) => {
                          const w = [...(data.websites || [''])];
                          w[i] = e.target.value;
                          const hasAnyUrl = w.some((u) => (u || '').trim().length > 0);
                          setData((d) => ({ ...d, websites: w, no_website: hasAnyUrl ? false : d.no_website }));
                        }}
                      />
                    ))}
                  </div>
                  <Button
                    variant="link"
                    className="px-0 mb-4 text-[#1C472B] hover:text-[#143620]"
                    onClick={() => setData((d) => ({
                      ...d,
                      websites: [...(d.websites || ['']), ''],
                      no_website: false,
                    }))}
                  >
                    + Add another website
                  </Button>
                  <label
                    htmlFor="no-web"
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all focus-within:ring-2 focus-within:ring-[#1C472B] focus-within:ring-offset-2 ${
                      data.no_website ? 'border-[#1C472B] bg-[#E6F7F3]/50' : 'border-slate-300 hover:border-[#A5E0D2]'
                    }`}
                  >
                    <Checkbox id="no-web" checked={data.no_website} onCheckedChange={(c) => setData((d) => ({ ...d, no_website: !!c }))} />
                    <span className="font-medium text-[#0F172A] flex-1">I don't have a website or online presence</span>
                  </label>
                </>
              )}

              {step === 2 && subStep === 3 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business details</h1>
                  <p className="text-slate-600 mb-4">Business location</p>
                  <p className="text-sm text-slate-600 mb-4">
                    Select up to 5 countries that your business has direct or indirect connections to.
                  </p>
                  <a href="#" className="text-[#1C472B] text-sm mb-4 block hover:text-[#143620] hover:underline">Which locations should I select?</a>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(data.business_countries || []).map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#E6F7F3] text-[#1C472B] text-sm font-medium"
                      >
                        {c}
                        <button
                          type="button"
                          onClick={() => setData((d) => ({ ...d, business_countries: (d.business_countries || []).filter((x) => x !== c) }))}
                          className="hover:text-[#143620] ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                    {COUNTRIES.filter((c) => !(data.business_countries || []).includes(c)).map((c) => (
                      <label
                        key={c}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-[#E6F7F3]/50 ${
                          (data.business_countries || []).length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Checkbox
                          checked={(data.business_countries || []).includes(c)}
                          onCheckedChange={(checked) => {
                            const cur = data.business_countries || [];
                            if (checked && cur.length < 5) setData((d) => ({ ...d, business_countries: [...cur, c] }));
                            else if (!checked) setData((d) => ({ ...d, business_countries: cur.filter((x) => x !== c) }));
                          }}
                        />
                        <span className="text-sm">{c}</span>
                      </label>
                    ))}
                  </div>
                  {(data.business_countries || []).length >= 5 && (
                    <p className="text-sm text-slate-500 mt-2">Maximum 5 countries selected.</p>
                  )}
                </>
              )}

              {step === 3 && subStep === 0 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business ownership</h1>
                  <p className="text-slate-600 mb-2">Beneficial owners</p>
                  <p className="text-sm text-slate-600 mb-4">
                    We are required to verify all beneficial owners. These are individuals who own 25% or more of the business and the officeholders of the business.
                  </p>
                  <a href="#" className="text-[#1C472B] text-sm mb-4 block hover:text-[#143620] hover:underline">How do I determine my beneficial owners?</a>
                  {individualsLoading ? (
                    <>
                      <p className="text-sm text-slate-600 mb-4">Please wait while we gather your details. This might take a few minutes.</p>
                      <Progress value={ownershipProgress} className="h-2 mb-6 bg-slate-200 [&>div]:bg-[#1C472B]" />
                      <Button
                        variant="link"
                        className="px-0 text-[#1C472B] hover:text-[#143620]"
                        disabled
                      >
                        + Add an individual
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-600 mb-4">
                        {data.beneficial_owners?.length ? "We've identified the following individuals. Please add or edit details where required." : "The ABR does not provide director names for companies. Please add beneficial owners and officeholders below."}
                      </p>
                      <div className="flex flex-col gap-4">
                        {data.beneficial_owners?.map((o, idx) => (
                          <div key={o.id} className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white shadow-sm">
                            <div className="w-10 h-10 rounded-full bg-[#E6F7F3] flex items-center justify-center text-[#1C472B] font-bold">
                              {o.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold">{o.name}</p>
                              <p className="text-sm text-slate-500">{o.role}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => openEditOwner(o, idx)}>Edit</Button>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="link"
                        className="px-0 mt-2 text-[#1C472B] hover:text-[#143620]"
                        onClick={() => setData((d) => ({
                          ...d,
                          beneficial_owners: [...(d.beneficial_owners || []), { id: String(Date.now()), name: 'New individual', role: 'Director' }],
                        }))}
                      >
                        + Add another individual
                      </Button>
                    </>
                  )}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <p className="font-medium text-slate-700 mb-2">Do any business owners hold shares or act on behalf of a third party?</p>
                    <p className="text-sm text-slate-600 mb-3">This includes holding shares as a nominee or conducting business activities for another party.</p>
                    <RadioGroup value={data.third_party_shareholding} onValueChange={(v) => setData((d) => ({ ...d, third_party_shareholding: v }))}>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <RadioGroupItem value="yes" id="tpy-yes" />
                          <span>Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <RadioGroupItem value="no" id="tpy-no" />
                          <span>No</span>
                        </label>
                      </div>
                    </RadioGroup>
                  </div>
                </>
              )}

              {step === 3 && subStep === 1 && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business ownership</h1>
                  <p className="text-slate-600 mb-4">Account applicant</p>
                  <p className="text-sm text-slate-600 mb-4">
                    To continue onboarding, we need to understand whether you are a beneficial owner or if you are applying on behalf of one.
                  </p>
                  <p className="font-medium mb-2">Which of the following individuals are you?</p>
                  <RadioGroup value={data.account_applicant_id} onValueChange={(v) => setData((d) => ({ ...d, account_applicant_id: v }))}>
                    <div className="flex flex-col gap-3">
                      {data.beneficial_owners?.map((o) => (
                        <div
                          key={o.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setData((d) => ({ ...d, account_applicant_id: o.id }))}
                          onKeyDown={(ev) => ev.key === 'Enter' && setData((d) => ({ ...d, account_applicant_id: o.id }))}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            data.account_applicant_id === o.id ? 'border-[#1C472B] bg-[#E6F7F3]/50' : 'border-slate-200 hover:border-[#A5E0D2]'
                          }`}
                        >
                          <RadioGroupItem value={o.id} id={`app-${o.id}`} />
                          <div className="w-10 h-10 rounded-full bg-[#E6F7F3] flex items-center justify-center text-[#1C472B] font-bold">
                            {o.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0F172A]">{o.name}</p>
                            <p className="text-sm text-slate-500">{o.role}</p>
                          </div>
                        </div>
                      ))}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setData((d) => ({ ...d, account_applicant_id: 'none' }))}
                        onKeyDown={(ev) => ev.key === 'Enter' && setData((d) => ({ ...d, account_applicant_id: 'none' }))}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          data.account_applicant_id === 'none' ? 'border-[#1C472B] bg-[#E6F7F3]/50' : 'border-slate-200 hover:border-[#A5E0D2]'
                        }`}
                      >
                        <RadioGroupItem value="none" id="app-none" />
                        <span className="font-medium text-[#0F172A]">I am none of the above</span>
                      </div>
                    </div>
                  </RadioGroup>
                  {data.account_applicant_id === 'none' && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={data.declaration_accepted}
                          onCheckedChange={(c) => setData((d) => ({ ...d, declaration_accepted: !!c }))}
                        />
                        <span className="text-sm text-slate-700">
                          I declare that I have been authorized to act on behalf of {data.business?.EntityName || 'this business'} and will be able to verify my authority upon account opening.
                        </span>
                      </label>
                    </div>
                  )}
                </>
              )}

              {step === 3 && subStep === 2 && !uboFormTarget && (
                <>
                  <h1 className="text-2xl font-bold mb-2">Business ownership</h1>
                  <p className="text-slate-600 mb-4">ID verification</p>
                  <p className="text-sm text-slate-600 mb-4">
                    Submit ID details for <strong>each individual</strong> to verify their identity. To update the list of individuals below, go back to previous steps and edit the list of business owners.
                  </p>
                  <div className="flex flex-col gap-3 mb-6">
                    {individualsToVerify.map((person) => {
                      const verified = data.ubo_verified?.[person.id];
                      return (
                        <div
                          key={person.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => !verified && openUboForm(person)}
                          onKeyDown={(e) => e.key === 'Enter' && !verified && openUboForm(person)}
                          className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            verified ? 'border-slate-200 bg-slate-50' : 'border-slate-200 hover:border-[#1C472B] hover:bg-[#E6F7F3]/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#E6F7F3] flex items-center justify-center text-[#1C472B] font-bold">
                              {(person.name || '?')[0]}
                            </div>
                            <div>
                              <p className="font-semibold text-[#0F172A]">{person.name}</p>
                              <p className="text-sm text-slate-500">{person.role}</p>
                            </div>
                          </div>
                          {verified ? (
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                              <CheckCircle className="w-4 h-4" /> Electronically verified
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 text-sm">To be submitted</span>
                              <Pencil className="w-4 h-4 text-[#1C472B]" />
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {step === 3 && subStep === 2 && uboFormTarget && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Button variant="ghost" size="sm" onClick={() => setUboFormTarget(null)} className="text-slate-600">← Back</Button>
                  </div>
                  <h1 className="text-2xl font-bold mb-2">ID verification</h1>
                  <p className="text-sm text-slate-600 mb-6">Complete all required information below.</p>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3">Your personal details</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        You need to complete an ID verification with a liveness check. Upon clicking Submit, you will be guided to take a photo of your original ID and a live selfie.
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-slate-700">Legal first name</Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.first ? 'border-amber-500' : ''}`}
                            value={uboIdForm.first}
                            onChange={(e) => { setUboIdForm((f) => ({ ...f, first: e.target.value })); if (uboIdFormErrors.first) setUboIdFormErrors((p) => ({ ...p, first: undefined })); }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.first && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.first}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Legal middle name <span className="text-slate-400 font-normal">(Optional)</span></Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.middle ? 'border-amber-500' : ''}`}
                            value={uboIdForm.middle}
                            onChange={(e) => { setUboIdForm((f) => ({ ...f, middle: e.target.value })); if (uboIdFormErrors.middle) setUboIdFormErrors((p) => ({ ...p, middle: undefined })); }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.middle && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.middle}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Legal last name</Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.last ? 'border-amber-500' : ''}`}
                            value={uboIdForm.last}
                            onChange={(e) => { setUboIdForm((f) => ({ ...f, last: e.target.value })); if (uboIdFormErrors.last) setUboIdFormErrors((p) => ({ ...p, last: undefined })); }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.last && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.last}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Date of birth</Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.dob ? 'border-amber-500' : ''}`}
                            placeholder="yyyy-mm-dd"
                            value={uboIdForm.dob}
                            onChange={(e) => {
                              const formatted = formatDobInput(e.target.value);
                              setUboIdForm((f) => ({ ...f, dob: formatted }));
                              if (uboIdFormErrors.dob) setUboIdFormErrors((p) => ({ ...p, dob: undefined }));
                            }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.dob && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.dob}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Nationality</Label>
                          <Select value={uboIdForm.nationality} onValueChange={(v) => { setUboIdForm((f) => ({ ...f, nationality: v })); if (uboIdFormErrors.nationality) setUboIdFormErrors((p) => ({ ...p, nationality: undefined })); }}>
                            <SelectTrigger className={`mt-1 ${uboIdFormErrors.nationality ? 'border-amber-500' : ''}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {uboIdFormErrors.nationality && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.nationality}</p>}
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3">Government ID</h3>
                      <p className="text-sm text-slate-600 mb-4">Select an ID issuance country or region first. You will then be prompted to enter ID details or upload an ID image.</p>
                      <div>
                        <Label className="text-slate-700">ID issuance country or region</Label>
                        <Select value={uboIdForm.id_country} onValueChange={(v) => { setUboIdForm((f) => ({ ...f, id_country: v })); if (uboIdFormErrors.id_country) setUboIdFormErrors((p) => ({ ...p, id_country: undefined })); }}>
                          <SelectTrigger className={`mt-1 ${uboIdFormErrors.id_country ? 'border-amber-500' : ''}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {uboIdFormErrors.id_country && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.id_country}</p>}
                      </div>
                      <div className="mt-4">
                        <Label className="text-slate-700">ID type</Label>
                        <Select value={uboIdForm.id_type} onValueChange={(v) => { setUboIdForm((f) => ({ ...f, id_type: v })); if (uboIdFormErrors.id_type) setUboIdFormErrors((p) => ({ ...p, id_type: undefined })); }}>
                          <SelectTrigger className={`mt-1 ${uboIdFormErrors.id_type ? 'border-amber-500' : ''}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Australian Driver Licence">Australian Driver Licence</SelectItem>
                            <SelectItem value="Australian Passport">Australian Passport</SelectItem>
                            <SelectItem value="Indonesian Identity Card (Kartu Tanda Penduduk)">Indonesian Identity Card (KTP)</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {uboIdFormErrors.id_type && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.id_type}</p>}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3">Address</h3>
                      <p className="text-sm text-slate-600 mb-4">Enter the address as it appears on your ID. If your ID does not include an address, enter your current residential address.</p>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-slate-700">Country or region</Label>
                          <Select value={uboIdForm.address?.country || ''} onValueChange={(v) => { setUboIdForm((f) => ({ ...f, address: { ...f.address, country: v } })); if (uboIdFormErrors.address_country) setUboIdFormErrors((p) => ({ ...p, address_country: undefined })); }}>
                            <SelectTrigger className={`mt-1 ${uboIdFormErrors.address_country ? 'border-amber-500' : ''}`}><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {uboIdFormErrors.address_country && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_country}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Address line 1</Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.address_line1 ? 'border-amber-500' : ''}`}
                            placeholder="123 Example Street"
                            value={uboIdForm.address?.line1 || ''}
                            onChange={(e) => { setUboIdForm((f) => ({ ...f, address: { ...f.address, line1: e.target.value } })); if (uboIdFormErrors.address_line1) setUboIdFormErrors((p) => ({ ...p, address_line1: undefined })); }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.address_line1 && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_line1}</p>}
                        </div>
                        <div>
                          <Label className="text-slate-700">Address line 2 <span className="text-slate-400 font-normal">(Optional)</span></Label>
                          <Input
                            className={`mt-1 ${uboIdFormErrors.address_line2 ? 'border-amber-500' : ''}`}
                            placeholder="Apt., suite, building #, etc."
                            value={uboIdForm.address?.line2 || ''}
                            onChange={(e) => { setUboIdForm((f) => ({ ...f, address: { ...f.address, line2: e.target.value } })); if (uboIdFormErrors.address_line2) setUboIdFormErrors((p) => ({ ...p, address_line2: undefined })); }}
                            onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                          />
                          {uboIdFormErrors.address_line2 && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_line2}</p>}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-slate-700">City</Label>
                            <Input
                              className={`mt-1 ${uboIdFormErrors.address_city ? 'border-amber-500' : ''}`}
                              value={uboIdForm.address?.city || ''}
                              onChange={(e) => { setUboIdForm((f) => ({ ...f, address: { ...f.address, city: e.target.value } })); if (uboIdFormErrors.address_city) setUboIdFormErrors((p) => ({ ...p, address_city: undefined })); }}
                              onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                            />
                            {uboIdFormErrors.address_city && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_city}</p>}
                          </div>
                          <div>
                            <Label className="text-slate-700">State or province</Label>
                            <Input
                              className={`mt-1 ${uboIdFormErrors.address_state ? 'border-amber-500' : ''}`}
                              value={uboIdForm.address?.state || ''}
                              onChange={(e) => { setUboIdForm((f) => ({ ...f, address: { ...f.address, state: e.target.value } })); if (uboIdFormErrors.address_state) setUboIdFormErrors((p) => ({ ...p, address_state: undefined })); }}
                              onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                            />
                            {uboIdFormErrors.address_state && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_state}</p>}
                          </div>
                          <div>
                            <Label className="text-slate-700">Postal code</Label>
                            <Input
                              className={`mt-1 ${uboIdFormErrors.address_postcode ? 'border-amber-500' : ''}`}
                              value={uboIdForm.address?.postcode || ''}
                              onChange={(e) => { const v = uboIdForm.address?.country === 'Australia' ? e.target.value.replace(/\D/g, '').slice(0, 4) : e.target.value; setUboIdForm((f) => ({ ...f, address: { ...f.address, postcode: v } })); if (uboIdFormErrors.address_postcode) setUboIdFormErrors((p) => ({ ...p, address_postcode: undefined })); }}
                              onBlur={() => setUboIdFormErrors((prev) => ({ ...prev, ...validateUboIdForm(uboIdForm).errors }))}
                            />
                            {uboIdFormErrors.address_postcode && <p className="text-xs text-amber-600 mt-1">{uboIdFormErrors.address_postcode}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {Object.keys(uboIdFormErrors).length > 0 && (
                    <p className="text-sm text-amber-600 mt-4">Please fix the highlighted fields before submitting.</p>
                  )}
                  <div className="flex gap-4 mt-8">
                    <Button variant="ghost" className="text-[#1C472B]" onClick={() => setUboFormTarget(null)}>Back</Button>
                    <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={saveUboFormLater} disabled={saving}>{saving ? 'Saving...' : 'Save for later'}</Button>
                    <Button
                      className="bg-[#1C472B] hover:bg-[#143620] text-white"
                      onClick={() => {
                        const { valid, errors } = validateUboIdForm(uboIdForm);
                        if (!valid) { setUboIdFormErrors(errors); toast.error('Please fix the highlighted fields before submitting.'); return; }
                        setUboIdFormErrors({});
                        setData((d) => ({ ...d, ubo_id_details: { ...(d.ubo_id_details || {}), [uboFormTarget.id]: { ...uboIdForm } } }));
                        setQrModal(uboFormTarget.id);
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                </>
              )}

              {!(step === 3 && subStep === 2 && uboFormTarget) && (
              <div className="mt-8 space-y-2">
                {!canProceedFromStep() && (
                  <p className="text-sm text-amber-600">Please complete all required fields to continue.</p>
                )}
                <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="h-11 px-6 rounded-xl border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-semibold"
                  onClick={() => {
                    if (step === 1) setStep(0);
                    else if (subStep > 0) setSubStep(subStep - 1);
                    else if (step > 1) { setStep(step - 1); setSubStep(step === 3 ? 3 : 0); }
                  }}
                >
                  Back
                </Button>
                <Button
                  className="h-11 px-6 rounded-xl bg-[#1C472B] hover:bg-[#143620] text-white font-semibold shadow-sm disabled:opacity-70"
                  disabled={!canProceedFromStep() || saving || individualsLoading}
                  onClick={() => {
                    if (step === 1 && data.business) setConfirmModal('business');
                    else if (step === 3 && subStep === 0) setConfirmModal('beneficial');
                    else if (step === 3 && subStep === 1 && data.account_applicant_id && data.account_applicant_id !== 'none') setConfirmModal('identity');
                    else saveAndContinue();
                  }}
                >
                  {individualsLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Please wait...
                    </>
                  ) : saving ? 'Saving...' : (step === 3 && subStep === 2 ? 'Next' : 'Save and continue')}
                </Button>
                </div>
              </div>
              )}
            </div>
        )}

        {isWelcome && (
          <div className="text-center max-w-xl mx-auto pt-4">
            <h1 className="text-3xl font-bold text-[#0F172A] mb-4">
              We're glad to have you here, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mb-8">
              Start getting more out of your payments by filling in a few details about your business.
            </p>
            <div className="text-left space-y-6 mb-8">
              <div className="flex gap-4">
                <Clock className="w-6 h-6 text-slate-400 shrink-0" />
                <div>
                  <p className="font-semibold">How long will this take?</p>
                  <p className="text-slate-600">Complete your business profile in 5-10 mins</p>
                </div>
              </div>
              <div className="flex gap-4">
                <FileText className="w-6 h-6 text-slate-400 shrink-0" />
                <div>
                  <p className="font-semibold">What information do I need?</p>
                  <p className="text-slate-600">You'll need your business details and government-issued documents</p>
                </div>
              </div>
            </div>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" className="h-11 rounded-xl border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-semibold" onClick={() => navigate('/dashboard')}>Cancel</Button>
              <Button className="h-11 px-6 rounded-xl bg-[#1C472B] hover:bg-[#143620] text-white font-semibold" onClick={() => setStep(1)}>
                Continue
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>

      <Dialog open={confirmModal === 'beneficial'} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="border-[#1C472B]/20">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Confirm beneficial owners</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Please make sure that you've provided details for all beneficial owners. You won't be able to add to this list after you confirm.
          </p>
          <div className="flex flex-col gap-3 mb-4">
            {data.beneficial_owners?.map((o) => (
              <div key={o.id} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="w-8 h-8 rounded-full bg-[#E6F7F3] text-[#1C472B] flex items-center justify-center text-sm font-semibold">
                  {(o.name || '?')[0]}
                </div>
                <div>
                  <p className="font-medium">{o.name}</p>
                  <p className="text-sm text-slate-500">{o.role}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setConfirmModal(null)}>Go back</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" onClick={() => { setConfirmModal(null); saveAndContinue(); }}>Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmModal === 'identity'} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="border-[#1C472B]/20">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Confirm identity</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            Please confirm that you are {data.beneficial_owners?.find((o) => o.id === data.account_applicant_id)?.name || 'this person'}. You won't be able to change this.
          </p>
          <p className="text-sm text-slate-600 mb-4">On the next step, you'll be asked to verify your identity.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setConfirmModal(null)}>Cancel</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" onClick={() => { setConfirmModal(null); saveAndContinue(); }}>Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrModal} onOpenChange={() => setQrModal(null)}>
        <DialogContent className="border-[#1C472B]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Complete ID verification with your mobile device</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Before you begin, have your government-issued ID ready. Scan the QR code below using your mobile camera or QR code app. You will be guided to take a photo of your ID and a live selfie. Make sure the photos are taken in good lighting without glare.
          </p>
          <p className="text-sm text-slate-600 mb-4">
            If you do not have your ID with you now, you can return to this step later and your progress will be saved. If you need further assistance, please <a href="#" className="text-[#1C472B] hover:underline">contact support</a>.
          </p>
          <div className="flex justify-center py-4 bg-slate-50 rounded-lg mb-4">
            <QRCodeSVG value={`https://payzen-verify.demo?id=${qrModal}`} size={180} />
          </div>
          <p className="text-sm text-slate-500 mb-4">QR code expires in 120 seconds.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" className="text-[#1C472B]" onClick={() => setQrModal(null)}>Cancel</Button>
            <Button variant="outline" className="border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900" onClick={simulateQrVerification}>I've completed verification</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!successModal} onOpenChange={(open) => !open && closeSuccessAndReturn()}>
        <DialogContent className="border-[#1C472B]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Complete ID verification with your mobile device</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Please scan the QR code below using your mobile camera or QR code app. You will be guided to take a photo of your government-issued ID and a live selfie.
          </p>
          <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50 flex items-center gap-3 mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 shrink-0" />
            <span className="font-medium text-green-800">Your identity has been electronically verified.</span>
          </div>
          <div className="flex justify-end">
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" onClick={closeSuccessAndReturn}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOwner} onOpenChange={(open) => !open && setEditOwner(null)}>
        <DialogContent className="border-[#1C472B]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Edit individual</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Select the individual's role. If the name displayed below is different from what's printed on the ID, you may change it later in the ID verification step.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {OWNER_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-700">Legal first name</Label>
                <Input className="mt-1" value={editForm.first} onChange={(e) => setEditForm((f) => ({ ...f, first: e.target.value }))} />
              </div>
              <div>
                <Label className="text-slate-700">Legal middle name <span className="text-slate-400 font-normal">(Optional)</span></Label>
                <Input className="mt-1" value={editForm.middle} onChange={(e) => setEditForm((f) => ({ ...f, middle: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Legal last name</Label>
              <Input className="mt-1" value={editForm.last} onChange={(e) => setEditForm((f) => ({ ...f, last: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={editForm.noLongerRelevant}
                onCheckedChange={(c) => setEditForm((f) => ({ ...f, noLongerRelevant: !!c }))}
              />
              <span className="text-sm text-slate-700">This person is no longer relevant to the business</span>
            </label>
          </div>
          <div className="flex gap-2 justify-end mt-6">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setEditOwner(null)}>Cancel</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" onClick={saveEditOwner}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmModal === 'business'} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="border-[#1C472B]/20">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Confirm business</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Please make sure that you've selected the correct business entity. You won't be able to change this after you confirm.
          </p>
          {data.business && (
            <div className="space-y-1 text-sm">
              <p><strong>ABN:</strong> {data.business.Abn}</p>
              <p><strong>Entity name:</strong> {data.business.EntityName}</p>
              <p><strong>Entity type:</strong> {data.business.EntityType}</p>
              <p><strong>Location:</strong> {data.business.Location}</p>
            </div>
          )}
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setConfirmModal(null)}>Go back</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white" onClick={() => { setConfirmModal(null); saveAndContinue(); }}>Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmModal === 'review'} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="max-w-lg border-[#1C472B]/20 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1C472B]">Review your information</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">Take a moment to review your details below before submitting.</p>
          <div className="space-y-4 mb-4">
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <h4 className="font-semibold text-slate-800 mb-2">Business entity</h4>
              <p className="text-sm text-slate-600">{data.business?.EntityName || '—'}</p>
              <p className="text-sm text-slate-600">ABN: {data.business?.Abn || '—'}</p>
            </div>
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <h4 className="font-semibold text-slate-800 mb-2">Business details</h4>
              <p className="text-sm text-slate-600"><strong>Industry:</strong> {data.industry || '—'}</p>
              <p className="text-sm text-slate-600"><strong>Monthly revenue:</strong> {data.monthly_revenue || '—'}</p>
              {data.products_services && <p className="text-sm text-slate-600 mt-1"><strong>Products/services:</strong> {(data.products_services || '').slice(0, 100)}{(data.products_services || '').length > 100 ? '...' : ''}</p>}
              {data.business_countries?.length > 0 && <p className="text-sm text-slate-600 mt-1"><strong>Business locations:</strong> {data.business_countries.join(', ')}</p>}
            </div>
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <h4 className="font-semibold text-slate-800 mb-2">Business ownership</h4>
              {data.beneficial_owners?.map((o) => <p key={o.id} className="text-sm text-slate-600">{o.name} – {o.role}</p>)}
              {data.third_party_shareholding && <p className="text-sm text-slate-600 mt-1"><strong>Third-party:</strong> {data.third_party_shareholding}</p>}
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-4">By submitting, I confirm that all information provided is true and correct.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]" onClick={() => setConfirmModal(null)}>Back</Button>
            <Button className="bg-[#1C472B] hover:bg-[#143620] text-white disabled:opacity-60" onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit'}</Button>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default Onboarding;
